import { Routes } from '@angular/router';
import { guestGuard } from '../Shared/Guards/guest.guard';
import { loggedInGuard } from '../Shared/Guards/logged-in.guard';
import { CallbackComponent } from '../Auth/Components/callback/callback.component';
import { ImpersonationExpiredComponent } from '../Auth/Components/impersonation-expired/impersonation-expired.component';
import { LoginComponent } from '../Auth/Components/login/login.component';
import { HomeComponent } from '../Workspace/Components/Pages/home/home.component';
import { AuthComponent } from '../Auth/auth/auth.component';
import { WorkspaceComponent } from '../Workspace/workspace/workspace.component';
import { DashboardComponent as UsersDashboardComponent } from '../Users/components/pages/dashboard/dashboard.component';
import { UserComponent } from '../User/user/user.component';
import { OverviewComponent as UserOverviewComponent } from '../User/Components/pages/overview/overview.component';
import { UsersComponent } from '../Users/users/users.component';
import { DevicesComponent } from '../Devices/devices/devices.component';
import { DashboardComponent as DevicesDashboardComponent } from '../Devices/components/pages/dashboard/dashboard.component';
import { DeviceComponent } from '../Device/device/device.component';
import { OverviewComponent as DeviceOverviewComponent } from '../Device/Components/pages/overview/overview.component';
import { HardwareComponent } from '../Device/Components/pages/hardware/hardware.component';
import { NetworkComponent } from '../Device/Components/pages/network/network.component';
import { SecurityComponent } from '../Device/Components/pages/security/security.component';
import { SettingsComponent as AppSettingsComponent } from '../Settings/settings/settings.component';
import { SettingsComponent } from '../Device/Components/pages/settings/settings.component';
import { LocalAdminComponent } from '../Device/Components/pages/local-admin/local-admin.component';
import { ConnectedDevicesComponent } from '../Device/Components/pages/connected-devices/connected-devices.component';
import { ApplicationsComponent } from '../Device/Components/pages/applications/applications.component';
import { ActivityLogComponent } from '../Device/Components/pages/activity-log/activity-log.component';
import { DevicesComponent as UserDevicesComponent } from '../User/Components/pages/devices/devices.component';
import { LicensesComponent as UserLicensesComponent } from '../User/Components/pages/licenses/licenses.component';
import { RolesManagementComponent } from '../Settings/Components/Pages/roles-management/roles-management.component';
import { UsersManagementComponent } from '../Settings/Components/Pages/users-management/users-management.component';
import { ListUsersFacade as IntuneListUsersFacade } from '../../Facades/Intune/list-users.facade';
import { ShowUserFacade as IntuneShowUserFacade } from '../../Facades/Intune/show-user.facade';
import { ShowDeviceFacade as IntuneShowDeviceFacade } from '../../Facades/Intune/show-device.facade';
import { ListDevicesFacade as IntuneListDevicesFacade } from '../../Facades/Intune/list-devices.facade';
import { ListUsersFacade } from '../../Facades/list-users.facade';
import { ListRolesFacade } from '../../Facades/list-roles.facade';
import { AutopilotDevicesHistoryComponent } from '../AutopilotDevices/Components/Pages/autopilot-devices-history/autopilot-devices-history.component';
import { AutopilotDevicesFacade } from '../../Facades/Intune/autopilot-devices.facade';
import { AutopilotDevicesHomeComponent } from '../AutopilotDevices/Components/Pages/autopilot-devices-home/autopilot-devices-home.component';
import { AutopilotDevicesComponent } from '../AutopilotDevices/autopilot-devices/autopilot-devices.component';
import { RoleFormComponent } from '../Settings/Components/Pages/role-form/role-form.component';
import { RoleFormFacade } from '../../Facades/role-form.facade';
import { UserCreateComponent } from '../Settings/Components/Pages/user-create/user-create.component';
import { UserEditComponent } from '../Settings/Components/Pages/user-edit/user-edit.component';
import { BillingComponent } from '../Settings/Components/Pages/billing/billing.component';
import { BillingFacade } from '../../Facades/billing.facade';
import { LicencesComponent } from '../Settings/Components/Pages/licences/licences.component';
import { LicencesFacade } from '../../Facades/licences.facade';
import { RequestAccessComponent } from '../Auth/Components/request-access/request-access.component';
import { AccessRequestFacade } from '../../Facades/access-request.facade';
import { permissionGuard } from '../Shared/Guards/permission.guard';
import { entitlementGuard } from '../Shared/Guards/entitlement.guard';
import { PERMISSIONS } from '../../Enums/permission.enum';
import { ReportsComponent } from '../Reports/reports/reports.component';
import { AnomaliesReportComponent } from '../Reports/components/Pages/anomalies-report/anomalies-report.component';
import { EntraAdminRolesReportComponent } from '../Reports/components/Pages/entra-admin-roles-report/entra-admin-roles-report.component';
import { M365LicenseAssignmentReportComponent } from '../Reports/components/Pages/m365-license-assignment-report/m365-license-assignment-report.component';
import { AnomaliesReportFacade } from '../../Facades/reports/anomalies.facade';
import { EntraAdminRolesReportFacade } from '../../Facades/reports/entra-admin-roles.facade';
import { M365LicenseReportFacade } from '../../Facades/reports/m365-license.facade';
import { ReportsOverviewComponent } from '../Reports/components/Pages/reports-overview/reports-overview.component';
import { HomeFacade } from '../../Facades/home.facade';
import { HomeSpotlightFacade } from '../../Facades/home-spotlight.facade';

export const routes: Routes = [
  {
    path: 'auth',
    component: AuthComponent,
    children: [
      { path: 'login', canActivate: [guestGuard], component: LoginComponent, title: 'shared.routes.app.login' },
      { path: 'callback', component: CallbackComponent, title: 'shared.routes.app.callback' },
      { path: 'impersonation-expired', component: ImpersonationExpiredComponent, title: 'shared.routes.app.impersonation_expired' },
      { path: '', redirectTo: 'login', pathMatch: 'full' },
    ],
  },
  {
    path: 'settings',
    canActivate: [loggedInGuard],
    component: AppSettingsComponent,
    title: 'shared.routes.app.settings',
    providers: [ListUsersFacade, ListRolesFacade],
    children: [
      {
        path: 'users',
        canActivate: [loggedInGuard, permissionGuard],
        data: { permission: PERMISSIONS.USER_READ },
        title: 'shared.routes.app.settings_users',
        children: [
          { path: '', component: UsersManagementComponent, pathMatch: 'full' },
          { path: 'add', component: UserCreateComponent, title: 'shared.routes.app.settings_users_add', canActivate: [permissionGuard], data: { permission: PERMISSIONS.USER_CREATE } },
          { path: ':id', component: UserEditComponent, title: 'shared.routes.app.settings_users_edit', canActivate: [permissionGuard, entitlementGuard], data: { permission: PERMISSIONS.USER_EDIT, entitlement: 'rbac' } },
        ]
      },
      {
        path: 'roles',
        canActivate: [loggedInGuard, permissionGuard, entitlementGuard],
        data: { permission: PERMISSIONS.ROLE_READ, entitlement: 'rbac' },
        title: 'shared.routes.app.settings_roles',
        children: [
          { path: '', component: RolesManagementComponent, pathMatch: 'full' },
          { path: 'create', component: RoleFormComponent, title: 'shared.routes.app.settings_roles_create', providers: [RoleFormFacade], canActivate: [permissionGuard], data: { permission: PERMISSIONS.ROLE_CREATE } },
          { path: 'edit/:id', component: RoleFormComponent, title: 'shared.routes.app.settings_roles_edit', providers: [RoleFormFacade], canActivate: [permissionGuard], data: { permission: PERMISSIONS.ROLE_EDIT } },
          { path: '**', redirectTo: '', pathMatch: 'full' },
        ],
      },
      {
        path: 'billing',
        canActivate: [loggedInGuard, permissionGuard],
        data: { permission: PERMISSIONS.BILLING_MANAGE },
        title: 'shared.routes.app.settings_billing',
        providers: [BillingFacade],
        component: BillingComponent,
      },
      {
        path: 'licences',
        canActivate: [loggedInGuard, permissionGuard],
        data: { permission: PERMISSIONS.LICENSE_MANAGE },
        title: 'shared.routes.app.settings_licences',
        providers: [LicencesFacade],
        component: LicencesComponent,
      },
      { path: '', redirectTo: 'users', pathMatch: 'full' },
      { path: '**', redirectTo: 'users', pathMatch: 'full' },
    ],
  },
  {
    path: 'request-access',
    component: AuthComponent,
    children: [
      {
        path: '',
        canActivate: [loggedInGuard],
        component: RequestAccessComponent,
        title: 'shared.routes.app.request_access',
        providers: [AccessRequestFacade],
      },
    ],
  },
  {
    canActivate: [loggedInGuard],
    path: '',
    component: WorkspaceComponent,
    children: [
      {
        path: 'home',
        canActivate: [loggedInGuard],
        component: HomeComponent,
        title: 'shared.routes.app.home',
        providers: [
          HomeFacade,
          HomeSpotlightFacade,
          AnomaliesReportFacade,
          EntraAdminRolesReportFacade,
          M365LicenseReportFacade,
        ],
      },
      {
        path: 'users',
        canActivate: [loggedInGuard, permissionGuard, entitlementGuard],
        component: UsersComponent,
        title: 'shared.routes.app.users',
        providers: [IntuneListUsersFacade],
        data: { permission: PERMISSIONS.INTUNE_USER_READ, entitlement: 'user_management' },
        children: [
          { path: '', component: UsersDashboardComponent, pathMatch: 'full' },
          {
            path: ':id',
            component: UserComponent,
            providers: [IntuneShowUserFacade],
            data: { secondaryNavTitle: 'users' },
            children: [
              {
                path: 'overview',
                component: UserOverviewComponent,
              },
              {
                path: 'overview', component: UserOverviewComponent, title: 'shared.routes.app.user_overview',
                data: { icon: 'dashboard', label: 'user_overview', viewInSecondaryNav: true },
              },
              {
                path: 'devices',
                component: UserDevicesComponent,
                title: 'shared.routes.app.user_devices',
                data: { icon: 'devices', label: 'user_devices', viewInSecondaryNav: true },
              },
              {
                path: 'licenses',
                component: UserLicensesComponent,
                title: 'shared.routes.app.user_licenses',
                data: { icon: 'workspace_premium', label: 'user_licenses', viewInSecondaryNav: true, },
              },
              { path: '', redirectTo: 'overview', pathMatch: 'full' },
            ],
          },
        ],
      },
      {
        path: 'devices',
        canActivate: [loggedInGuard, permissionGuard, entitlementGuard],
        component: DevicesComponent,
        title: 'shared.routes.app.devices',
        providers: [IntuneListDevicesFacade],
        data: { permission: PERMISSIONS.DEVICE_READ, entitlement: 'device_management' },
        children: [
          { path: '', component: DevicesDashboardComponent, pathMatch: 'full' },
          {
            path: ':id',
            component: DeviceComponent,
            providers: [IntuneShowDeviceFacade],
            data: { secondaryNavTitle: 'devices' },
            children: [
              {
                path: 'overview',
                component: DeviceOverviewComponent,
                title: 'shared.routes.app.device_overview',
                data: { icon: 'dashboard', label: 'device_overview', viewInSecondaryNav: true },
              },
              {
                path: 'hardware',
                component: HardwareComponent,
                title: 'shared.routes.app.device_hardware',
                data: { icon: 'memory', label: 'device_hardware', viewInSecondaryNav: true },
              },
              {
                path: 'network',
                component: NetworkComponent,
                title: 'shared.routes.app.device_network',
                data: { icon: 'wifi', label: 'device_network', viewInSecondaryNav: true },
              },
              {
                path: 'security',
                component: SecurityComponent,
                title: 'shared.routes.app.device_security',
                data: { icon: 'security', label: 'device_security', viewInSecondaryNav: true },
              },
              {
                path: 'settings',
                component: SettingsComponent,
                title: 'shared.routes.app.device_settings',
                data: { icon: 'settings', label: 'device_settings', viewInSecondaryNav: true },
              },
              {
                path: 'local-admin',
                component: LocalAdminComponent,
                title: 'shared.routes.app.device_local_admin',
                data: {
                  icon: 'admin_panel_settings',
                  label: 'device_local_admin',
                  viewInSecondaryNav: true,
                },
              },
              {
                path: 'connected-devices',
                component: ConnectedDevicesComponent,
                title: 'shared.routes.app.device_connected_devices',
                data: {
                  icon: 'devices_other',
                  label: 'device_connected_devices',
                  viewInSecondaryNav: true,
                },
              },
              {
                path: 'applications',
                component: ApplicationsComponent,
                title: 'shared.routes.app.device_applications',
                data: { icon: 'apps', label: 'device_applications', viewInSecondaryNav: true },
              },
              {
                path: 'activity-log',
                component: ActivityLogComponent,
                title: 'shared.routes.app.device_activity_log',
                data: { icon: 'history', label: 'device_activity_log', viewInSecondaryNav: true },
              },
              { path: '', redirectTo: 'overview', pathMatch: 'full' },
            ],
          },
        ],
      },
      {
        path: 'autopilot-devices',
        canActivate: [loggedInGuard, permissionGuard, entitlementGuard],
        component: AutopilotDevicesComponent,
        providers: [AutopilotDevicesFacade],
        data: { permission: [PERMISSIONS.AUTOPILOT_DEVICES_READ, PERMISSIONS.AUTOPILOT_DEVICES_IMPORT], entitlement: 'autopilot' },
        children: [
          {
            path: 'history',
            canActivate: [loggedInGuard, permissionGuard],
            component: AutopilotDevicesHistoryComponent,
            title: 'shared.routes.app.autopilot_devices_history',
            data: { permission: PERMISSIONS.AUTOPILOT_DEVICES_IMPORT }
          },
          {
            path: '',
            title: 'shared.routes.app.autopilot_devices',
            component: AutopilotDevicesHomeComponent,
          },
          { path: '**', redirectTo: '', pathMatch: 'full' },
        ],
      },
      {
        path: 'reports',
        canActivate: [loggedInGuard, entitlementGuard],
        component: ReportsComponent,
        title: 'shared.routes.app.reports',
        providers: [AnomaliesReportFacade, EntraAdminRolesReportFacade, M365LicenseReportFacade],
        data: { secondaryNavTitle: 'reports', permission: PERMISSIONS.INTUNE_USER_READ, entitlement: 'reports' },
        children: [
          {
            path: 'anomalies', component: AnomaliesReportComponent, title: 'shared.routes.app.anomalies_report',
            data: { permission: PERMISSIONS.ANOMALIES_REPORT, icon: 'bar_chart', label: 'anomalies_report', viewInSecondaryNav: true },
            canActivate: [permissionGuard],
          },
          {
            path: 'entra-admin-roles', component: EntraAdminRolesReportComponent, title: 'shared.routes.app.entra_admin_roles_report',
            data: { permission: PERMISSIONS.ENTRA_ADMIN_REPORT, icon: 'admin_panel_settings', label: 'entra_admin_roles_report', viewInSecondaryNav: true },
            canActivate: [permissionGuard],
          },
          {
            path: 'm365-license-assignment', component: M365LicenseAssignmentReportComponent, title: 'shared.routes.app.m365_license_assignment_report',
            data: { permission: PERMISSIONS.M365_LICENSE_REPORT, icon: 'badge', label: 'm365_license_assignment_report', viewInSecondaryNav: true },
            canActivate: [permissionGuard],
          },
          { path: '', component: ReportsOverviewComponent, title: 'shared.routes.app.reports', pathMatch: 'full' },
        ],
      },
      { path: '**', redirectTo: 'home', pathMatch: 'full' },
      { path: '', redirectTo: 'home', pathMatch: 'full' },
    ],
  },
  { path: '**', redirectTo: 'home', pathMatch: 'full' },
];
